import React from 'react';
import { Tooltip } from 'antd';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import ImpressionTimeline from './ImpressionsTimeLine';

import { formatCurrency2SigFig } from '../../../../../../../../core/utils/campaigns';
import DataTable from '../../../../../../../shared/designSystem/components/DataTable';
import AttributionIcon from '../../../../../../../shared/designSystem/components/AttributionIcon';

// =============================================================================
// TransactionsTable — v2 DataTable migration with SERVER-SIDE pagination + sort.
//
// Pagination, sort field, and sort order all live in the URL so bookmarks and
// back-nav work:
//   ?page=N&pageSize=M&sortField=<orderId|lastImpression|totalImpressions|
//                                  transactionDate|revenue>&sortOrder=<ascend|descend>
//
// The contract matches `TransactionsTabLoader.js`, which turns those params
// into the GraphQL `orderBy` variable (e.g. `revenue_DESC`). `sortOrder`
// uses AntD's `ascend`/`descend` vocabulary for backwards compat with any
// existing URL share.
//
// Rows expand to show an ImpressionTimeline when totalImpressions > 0.
// Attribution-method icon column shows the match type (cookie / fingerprint /
// device / IP) as a small tooltipped avatar.
// =============================================================================

const SORTABLE_FIELDS = [
  'orderId',
  'lastImpression',
  'totalImpressions',
  'transactionDate',
  'revenue',
];

// Convert URL params to TanStack's sort state shape.
const sortingFromUrl = (queryParams) => {
  const field = queryParams.get('sortField');
  const order = queryParams.get('sortOrder');
  if (!SORTABLE_FIELDS.includes(field)) return [];
  if (order !== 'ascend' && order !== 'descend') return [];
  return [{ id: field, desc: order === 'descend' }];
};

const TransactionsTable = ({
  transactionData = [],
  transactionsPagination = { total: 0, hasNextPage: false },
  getImpressions,
}) => {
  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;
  const pageSize = parseInt(queryParams.get('pageSize')) || 10;
  const sorting = sortingFromUrl(queryParams);

  const handlePaginationChange = ({ pageIndex, pageSize: newPageSize }) => {
    const params = new URLSearchParams(location.search);
    if (newPageSize !== pageSize) {
      // Page size changed — reset to page 1 so we don't jump past the end
      // of the new slicing.
      params.set('page', '1');
      params.set('pageSize', newPageSize.toString());
    } else {
      params.set('page', (pageIndex + 1).toString());
      params.set('pageSize', pageSize.toString());
    }
    history.push({ search: params.toString() });
  };

  const handleSortingChange = (next) => {
    const params = new URLSearchParams(location.search);
    if (Array.isArray(next) && next.length > 0) {
      const { id, desc } = next[0];
      params.set('sortField', id);
      params.set('sortOrder', desc ? 'descend' : 'ascend');
    } else {
      params.delete('sortField');
      params.delete('sortOrder');
    }
    // Sort changed → reset to page 1 so the user sees the new top of the order.
    params.set('page', '1');
    params.set('pageSize', pageSize.toString());
    history.push({ search: params.toString() });
  };

  const columns = [
    {
      id: 'orderId',
      accessorKey: 'orderId',
      header: 'Order',
      sortable: true,
      meta: { align: 'left', width: 160 },
      cell: ({ getValue }) => {
        const v = getValue();
        if (!v) return <span className="campaign-overview__creatives-name">N/A</span>;
        return (
          <Tooltip overlayClassName="mjl-ds" title={v}>
            <span
              className="campaign-overview__creatives-name"
              style={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {v}
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: 'lastImpression',
      accessorKey: 'lastImpression',
      header: 'Last Impression',
      sortable: true,
      meta: { align: 'left', width: 180 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? moment(v).format('MM/DD/YY HH:mm') : '';
      },
    },
    {
      id: 'totalImpressions',
      accessorKey: 'totalImpressions',
      header: 'Impressions',
      sortable: true,
      meta: { align: 'right', width: 120 },
      cell: ({ getValue }) => getValue() || 0,
    },
    {
      id: 'transactionDate',
      accessorKey: 'transactionDate',
      header: 'Ordered on',
      sortable: true,
      meta: { align: 'left', width: 160 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? moment(v).format('MM/DD/YY HH:mm') : '';
      },
    },
    {
      id: 'revenue',
      accessorKey: 'revenue',
      header: 'Total',
      sortable: true,
      meta: { align: 'right', width: 120 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v && !isNaN(v) ? formatCurrency2SigFig(v) : formatCurrency2SigFig(0);
      },
    },
    {
      id: 'attrMethod',
      accessorKey: 'attrMethod',
      header: '',
      sortable: false,
      meta: { align: 'center', width: 70 },
      cell: ({ getValue }) => <AttributionIcon method={getValue()} />,
    },
  ];

  // Shared mobile card chrome — `campaign-overview__transactions-card-*`
  // BEM lifted out of `_transactions-v1.scss` so V1 + V2 render the same
  // chip pattern: ID header (mono semibold) + AttributionIcon, muted
  // timestamp, chip-row separated by border-top.
  const renderMobileCard = (record) => {
    const total =
      record.revenue && !isNaN(record.revenue) ? record.revenue : 0;
    return (
      <>
        <div className="campaign-overview__transactions-card-head">
          {record.orderId ? (
            <Tooltip overlayClassName="mjl-ds" title={record.orderId}>
              <div className="campaign-overview__transactions-card-id">
                {record.orderId}
              </div>
            </Tooltip>
          ) : (
            <div className="campaign-overview__transactions-card-id">N/A</div>
          )}
          <AttributionIcon method={record.attrMethod} />
        </div>

        <div className="campaign-overview__transactions-card-time">
          {record.transactionDate
            ? moment(record.transactionDate).format('MMM DD, YYYY · HH:mm')
            : '—'}
        </div>

        <div className="campaign-overview__transactions-card-chips">
          <span className="campaign-overview__transactions-card-chip">
            <span className="campaign-overview__transactions-card-chip-label">Total</span>
            <span className="campaign-overview__transactions-card-chip-value">
              {formatCurrency2SigFig(total)}
            </span>
          </span>
          <span className="campaign-overview__transactions-card-chip">
            <span className="campaign-overview__transactions-card-chip-label">
              Impressions
            </span>
            <span className="campaign-overview__transactions-card-chip-value">
              {record.totalImpressions || 0}
            </span>
          </span>
          <span className="campaign-overview__transactions-card-chip">
            <span className="campaign-overview__transactions-card-chip-label">
              Last impression
            </span>
            <span className="campaign-overview__transactions-card-chip-value">
              {record.lastImpression
                ? moment(record.lastImpression).format('MMM DD, HH:mm')
                : '—'}
            </span>
          </span>
        </div>
      </>
    );
  };

  return (
    <DataTable
      data={transactionData}
      columns={columns}
      pageSize={pageSize}
      pageSizeOptions={[10, 20, 50, 100]}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      manualPagination
      pageIndex={currentPage - 1}
      totalRows={transactionsPagination.total}
      onPaginationChange={handlePaginationChange}
      manualSorting
      sorting={sorting}
      onSortingChange={handleSortingChange}
      expandable={{
        isRowExpandable: (record) => record.totalImpressions > 0,
        renderExpandedRow: (record) => (
          <ImpressionTimeline transactionId={record.id} getImpressions={getImpressions} />
        ),
      }}
      emptyState="No transactions match the current filter."
    />
  );
};

export default TransactionsTable;
